import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api, API } from "@/lib/api";
import { STATUTE_TEXT } from "@/components/ComplianceStatuteModal";

// ============================================================================
// Unified CPA Handoff PDF — stitches Retirement Runway, Backdoor Roth
// playbook, and every Audit-Safe receipt (with statute text) into ONE
// bank-grade PDF suitable for an IRS auditor or a CPA year-end handoff.
// All client-side; hits /api/receipts + /api/mileage for live data.
// ============================================================================
const fmt = (n) => "$" + Number(Math.round(Number(n) || 0)).toLocaleString();

const ROTH_STEPS = [
  ["Open a Traditional IRA", "Establish a traditional IRA at your custodian if one does not exist."],
  ["Non-Deductible Contribution", "Contribute up to the annual IRA limit as a non-deductible contribution — creates basis."],
  ["File IRS Form 8606", "CPA files Form 8606 for the contribution year to track basis."],
  ["Short Waiting Interval", "Wait a business day or two before converting to reduce step-transaction scrutiny."],
  ["Convert to Roth IRA", "Direct-transfer traditional IRA balance to Roth. Pro-rata rule applies (IRC § 408(d)(2))."],
  ["Reconcile at Tax Time", "Report on Form 8606 Part II per Notice 2014-54 basis tracking."],
];

async function fetchImageAsDataUrl(imagePath) {
  if (!imagePath) return null;
  try {
    const res = await fetch(`${API}/files/${imagePath}`, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

export async function generateUnifiedHandoff({ runway = {}, roth = {}, onProgress } = {}) {
  const notify = (m) => { try { onProgress?.(m); } catch { /* noop */ } };
  notify("Fetching receipt ledger…");
  const [receiptsRes, mileageRes] = await Promise.all([
    api.get("/receipts").catch(() => ({ data: [] })),
    api.get("/mileage").catch(() => ({ data: {} })),
  ]);
  const receipts = Array.isArray(receiptsRes?.data) ? receiptsRes.data : [];
  const auditSafe = receipts
    .filter((r) => r?.compliance_verification_token?.badge === "audit-safe")
    .slice(0, 40); // cap for bundle size
  const mileage = mileageRes?.data || {};

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  const now = new Date();
  const stamp = now.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  // ---------- COVER ----------
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, W, 110, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(24);
  doc.text("Unified CPA Handoff Packet", M, 52);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.setTextColor(103, 232, 249);
  doc.text(`Compiled ${stamp} · Receipt Sheets`, M, 74);
  doc.setTextColor(148, 163, 184); doc.setFontSize(10);
  doc.text(`${auditSafe.length} audit-safe receipts · mileage $${Number(mileage.total_deduction || 0).toFixed(2)} · runway + Roth playbook`, M, 92);
  doc.setTextColor(0);

  // Section 1 — Retirement Runway summary
  let y = 150;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("1 · Retirement Runway Snapshot", M, y); y += 8;
  doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 20;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  const runwayRows = [
    ["Net 1099 income (input)",           fmt(runway.net || 0)],
    ["Retirement sweep %",                `${runway.pct || 0}%`],
    ["Solo 401(k) contribution",          fmt(runway.contribution || 0)],
    ["HSA contribution",                  fmt(runway.hsaContribution || 0)],
    ["Business mileage deduction",        fmt(runway.mileageDeduction || 0)],
    ["Fed + SE tax · before",             fmt(runway.totalBefore || 0)],
    ["Fed + SE tax · after",              fmt(runway.totalAfter || 0)],
    ["Total tax cut",                     fmt(runway.savings || 0)],
  ];
  runwayRows.forEach(([k, v]) => {
    doc.text(k, M, y); doc.text(v, W - M, y, { align: "right" }); y += 16;
  });
  doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110);
  doc.text("Statute stack: IRC § 402(g), § 415(c), § 223, § 1401(a).", M, y + 6);
  doc.setTextColor(0);

  // Section 2 — Backdoor Roth playbook
  doc.addPage();
  y = 60;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("2 · Backdoor Roth Playbook", M, y); y += 8;
  doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  const rothMeta = [
    ["Filing status",     roth.filing === "mfj" ? "Married filing jointly" : "Single"],
    ["Modified AGI",      fmt(roth.income || 0)],
    ["Determination",     roth.determination || "—"],
  ];
  rothMeta.forEach(([k, v]) => { doc.text(k, M, y); doc.text(v, W - M, y, { align: "right" }); y += 16; });
  y += 6;
  ROTH_STEPS.forEach(([t, b], i) => {
    doc.setFont("helvetica", "bold"); doc.text(`${i + 1}. ${t}`, M, y); y += 14;
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(b, W - M * 2);
    doc.text(lines, M, y); y += lines.length * 13 + 6;
  });
  doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110);
  doc.text("Statute basis: IRC § 408A(c)(3), § 408(d)(2), Notice 2014-54, Form 8606.", M, y + 8);
  doc.setTextColor(0);

  // Section 3 — Mileage summary
  doc.addPage();
  y = 60;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("3 · Mileage Vault Summary", M, y); y += 8;
  doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  const mRows = [
    ["Total trips",              `${(mileage.trips || []).length}`],
    ["Total miles",              `${Number(mileage.total_miles || 0).toLocaleString()}`],
    ["Total deduction",          fmt(mileage.total_deduction || 0)],
    ["Business (→401k pipeline)",fmt(mileage.business_mileage_deduction || 0)],
  ];
  mRows.forEach(([k, v]) => { doc.text(k, M, y); doc.text(v, W - M, y, { align: "right" }); y += 16; });

  // Section 4 — Audit-Safe receipt ledger table
  y += 12;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(`4 · Audit-Safe Receipt Ledger (${auditSafe.length})`, M, y); y += 8;
  doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 12;
  autoTable(doc, {
    startY: y,
    head: [["Date", "Merchant", "Category", "Statute", "Total"]],
    body: auditSafe.map((r) => [
      r.date || "—",
      (r.merchant || "—").slice(0, 24),
      r.category || "—",
      r.compliance_verification_token?.code || "—",
      `$${Number(r.total || 0).toFixed(2)}`,
    ]),
    theme: "grid",
    headStyles: { fillColor: [16, 185, 129], textColor: 15, fontStyle: "bold" },
    bodyStyles: { fontSize: 9, textColor: 40 },
    alternateRowStyles: { fillColor: [245, 250, 248] },
    columnStyles: { 4: { halign: "right", fontStyle: "bold" } },
    margin: { left: M, right: M },
  });

  // Section 5 — Statute reference index (unique codes actually used above)
  doc.addPage();
  y = 60;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("5 · Statute Reference Index", M, y); y += 8;
  doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
  const usedCodes = new Set(auditSafe.map((r) => r.compliance_verification_token?.code).filter(Boolean));
  usedCodes.forEach((code) => {
    const s = STATUTE_TEXT[code];
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`${code} · ${s?.heading || "IRS Compliance Statute"}`, M, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const body = s?.body || "Category not currently mapped to a specific IRS statute in our compliance registry.";
    const lines = doc.splitTextToSize(body, W - M * 2);
    if (y + lines.length * 12 > H - 80) { doc.addPage(); y = 60; }
    doc.text(lines, M, y); y += lines.length * 12 + 6;
    if (s?.reg_ref) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110);
      doc.text(`Reg refs: ${s.reg_ref}`, M, y);
      doc.setTextColor(0);
      y += 16;
    } else { y += 6; }
  });

  // Footer disclaimer on every page
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(120);
    doc.text(
      `Receipt Sheets · Unified CPA Handoff Packet · Page ${p} of ${pageCount} · Not legal or tax advice — confirm with a qualified CPA.`,
      M, H - 24,
    );
  }

  const fname = `unified_cpa_handoff_${now.toISOString().slice(0, 10)}.pdf`;
  notify(`Saving ${fname}…`);
  doc.save(fname);
  return { fname, receipt_count: auditSafe.length, mileage_total: Number(mileage.total_deduction || 0) };
}
